// Reglas puras del dominio. Las usan la interna, el portal en modo demo, el script de carga y las pruebas.
// Las mismas reglas viven en SQL (supabase/migrations/20260927120200_agencia_portal.sql y _agencia_acciones.sql);
// si cambias una, cambia la otra y corre `npm run test:sql` y `npm run test:recorridos`.
import type {
  Candidato,
  CandidatoPortal,
  Columna,
  CriterioCandidato,
  CriterioVacante,
  Etapa,
  VacanteInicio,
  Veredicto,
} from "./tipos";

export const ETIQUETA_COLUMNA: Record<Columna, string> = {
  cumple: "Cumple",
  contactado: "Contactado",
  entrevistado: "Entrevistado",
  contratado: "Contratado",
  revisar: "Revisar",
  algun_dia: "Algún día / Tal vez",
  descartado: "Descartados",
};
/**
 * Orden de las columnas en TODO lado (Tablero, ficha, «Mover a…», selector y orden por etapa de la Lista, y la
 * interna). Andrés, 28-sep: Cumple y Revisar juntas al principio (son los viables que llegan del filtro, sin
 * contactar), luego Contactado, Entrevistado, Contratado, Algún día / Tal vez (guardados «por si las moscas»,
 * 28-sep) y al final Descartados.
 */
export const COLUMNAS: Columna[] = [
  "cumple",
  "revisar",
  "contactado",
  "entrevistado",
  "contratado",
  "algun_dia",
  "descartado",
];
/** Posición de una columna en ese orden (para ordenar «por etapa»). */
export const ordenColumna = (c: Columna): number => COLUMNAS.indexOf(c);

/** Milisegundos de una fecha ISO (acepta «Z» y «+00:00»); 0 si no se puede leer. */
const ms = (t: string | null | undefined): number => {
  const n = t ? Date.parse(t) : NaN;
  return Number.isNaN(n) ? 0 : n;
};
/**
 * Los más recientes primero: recibido_en desc, luego ingresado_en desc (la hora real en que entró; la hoja solo
 * trae la fecha) y al final el id, para que el orden sea estable. Espejo del ORDER BY de SQL `portal_candidatos()`.
 */
export function masRecientesPrimero<T extends { id: string; recibido_en: string; ingresado_en?: string | null }>(
  a: T,
  z: T,
): number {
  return (
    ms(z.recibido_en) - ms(a.recibido_en) ||
    ms(z.ingresado_en ?? z.recibido_en) - ms(a.ingresado_en ?? a.recibido_en) ||
    (a.id < z.id ? -1 : a.id > z.id ? 1 : 0)
  );
}

const TZ = "America/Monterrey";
const HORA_MTY = new Intl.DateTimeFormat("es-MX", { timeZone: TZ, hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
const DIA_MTY = new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" });
/**
 * «28 sep · 14:55» (hora de Monterrey) cuando se sabe la hora; «28 sep» cuando no.
 * Una fecha sola de la hoja llega como medianoche (UTC, o de Monterrey según la base): eso NO es una hora.
 * Entonces la hora sale de ingresado_en (cuándo entró a la base) si cae ese mismo día en Monterrey.
 */
export function fechaHora(recibido: string | null | undefined, ingresado?: string | null): string {
  const p = partesFecha(recibido, ingresado);
  if (!p) return recibido ? fechaCorta(recibido) : "—";
  return p.hora ? `${fechaCorta(p.dia)} · ${p.hora}` : fechaCorta(p.dia);
}

/** Día (AAAA-MM-DD, Monterrey) y hora (HH:MM o null si no se sabe) de una llegada. null si no se puede leer. */
function partesFecha(recibido: string | null | undefined, ingresado?: string | null): { dia: string; hora: string | null } | null {
  if (!recibido) return null;
  const d = new Date(recibido);
  if (Number.isNaN(d.getTime())) return null;
  const medianocheUtc = d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0;
  const medianocheMty = d.getUTCSeconds() === 0 && HORA_MTY.format(d) === "00:00";
  if (!medianocheUtc && !medianocheMty) return { dia: DIA_MTY.format(d), hora: HORA_MTY.format(d) };
  const dia = medianocheUtc ? d.toISOString().slice(0, 10) : DIA_MTY.format(d);
  const i = ingresado ? new Date(ingresado) : null;
  if (i && !Number.isNaN(i.getTime()) && DIA_MTY.format(i) === dia) return { dia, hora: HORA_MTY.format(i) };
  return { dia, hora: null };
}

/**
 * La misma fecha que `fechaHora`, dicha como se platica: «hoy 17:15», «ayer 11:14», «2 oct 11:12», «28 sep».
 * Hoy y ayer se cuentan en hora de Monterrey. `ahora` es para las pruebas.
 */
export function fechaAmigable(recibido: string | null | undefined, ingresado?: string | null, ahora: Date = new Date()): string {
  const p = partesFecha(recibido, ingresado);
  if (!p) return recibido ? fechaCorta(recibido) : "—";
  const hoy = DIA_MTY.format(ahora);
  const ayer = DIA_MTY.format(new Date(Date.parse(hoy + "T12:00:00Z") - 86_400_000));
  const dia = p.dia === hoy ? "hoy" : p.dia === ayer ? "ayer" : fechaCorta(p.dia);
  return p.hora ? `${dia} ${p.hora}` : dia;
}

export const ETIQUETA_VEREDICTO: Record<Veredicto, string> = {
  cumple: "Cumple",
  revisar: "Revisar",
  descartado: "Descartado",
};
export const ETIQUETA_ETAPA: Record<Etapa, string> = {
  nuevo: "Nuevo",
  contactado: "Contactado",
  entrevistado: "Entrevistado",
  contratado: "Contratado",
  algun_dia: "Algún día / Tal vez",
  descartado: "Descartado",
};
export const ETAPAS: Etapa[] = [
  "nuevo",
  "contactado",
  "entrevistado",
  "contratado",
  "algun_dia",
  "descartado",
];

/** En qué columna del tablero cae un candidato. Espejo de SQL `portal_columna()`. */
export function columnaDe(veredicto: Veredicto, etapa: Etapa): Columna {
  if (etapa === "descartado") return "descartado";
  if (etapa === "algun_dia") return "algun_dia";
  if (etapa === "contactado" || etapa === "entrevistado" || etapa === "contratado")
    return etapa;
  // etapa nuevo: manda el veredicto del bot
  return veredicto === "cumple"
    ? "cumple"
    : veredicto === "revisar"
      ? "revisar"
      : "descartado";
}

/**
 * Pestañas del portal. Activos = Cumple, Contactado, Entrevistado y Revisar (etapas Sin contactar, Contactado
 * y Entrevistado). «Algún día / Tal vez» va aparte: NO cuenta como activo. Espejo de SQL `portal_grupo()`.
 */
export type Grupo = "activos" | "algun_dia" | "contratados" | "descartados";
export const GRUPOS: Grupo[] = ["activos", "algun_dia", "contratados", "descartados"];
export function grupoDe(col: Columna): Grupo {
  if (col === "algun_dia") return "algun_dia";
  return col === "contratado" ? "contratados" : col === "descartado" ? "descartados" : "activos";
}

/** Motivos con los que el cliente descarta desde el portal («Otro» lleva texto: «Otro: …»). */
export const MOTIVOS_DESCARTE_CLIENTE = [
  "No contestó",
  "No le interesó",
  "No se presentó",
  "No pasó la entrevista",
  "Ya encontró trabajo",
] as const;
export const MOTIVO_OTRO = "Otro";

/** Números de la tarjeta de Inicio de una vacante. Espejo de SQL `portal_inicio()`. */
export function resumenVacante(
  v: { id: string; puesto: string; zona: string },
  ks: (Pick<Candidato, "veredicto" | "etapa" | "recibido_en"> & { ingresado_en?: string | null })[],
  desde: string | null,
): VacanteInicio {
  const g = (k: (typeof ks)[number]) => grupoDe(columnaDe(k.veredicto, k.etapa));
  const act = ks.filter((k) => g(k) === "activos");
  const sin = act.filter((k) => k.etapa === "nuevo");
  const n = (f: (k: (typeof ks)[number]) => boolean) => ks.filter(f).length;
  return {
    id: v.id,
    puesto: v.puesto,
    zona: v.zona,
    // nuevos desde tu última visita: cuándo entró a la base (ingresado_en), no la fecha de la hoja
    nuevos: act.filter((k) => !desde || ms(k.ingresado_en ?? k.recibido_en) > ms(desde)).length,
    cumple: n((k) => k.veredicto === "cumple"),
    revisar: n((k) => k.veredicto === "revisar"),
    descartado: n((k) => k.veredicto === "descartado"),
    por_marcar: n((k) => k.etapa === "contactado"),
    entrevistados: n((k) => k.etapa === "entrevistado" || k.etapa === "contratado"),
    contratados: n((k) => k.etapa === "contratado"),
    total: ks.length,
    activos: act.length,
    sin_contactar: sin.length,
    sin_contactar_cumple: sin.filter((k) => k.veredicto === "cumple").length,
    sin_contactar_revisar: sin.filter((k) => k.veredicto === "revisar").length,
    contactados: n((k) => k.etapa === "contactado"),
    en_entrevista: n((k) => k.etapa === "entrevistado"),
    algun_dia: n((k) => g(k) === "algun_dia"),
    descartados: n((k) => g(k) === "descartados"),
    descartados_por_ti: n((k) => k.etapa === "descartado"),
  };
}

/** Qué etapa se guarda cuando el cliente suelta la tarjeta en una columna. */
export function etapaDeColumna(col: Columna): Etapa {
  if (col === "cumple" || col === "revisar") return "nuevo";
  return col;
}

/** ISO local (AAAA-MM-DD) de hoy. */
export function hoyISO(d = new Date()): string {
  const z = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
}

export function sumarDias(iso: string, n: number): string {
  const d = new Date(iso.slice(0, 10) + "T12:00:00");
  d.setDate(d.getDate() + n);
  return hoyISO(d);
}

/** Candado de pago. Espejo de SQL `portal_bloqueado()`: pagado_hasta vencido = bloqueado; null = sin candado. */
export function estaBloqueado(
  pagado_hasta: string | null | undefined,
  hoy = hoyISO(),
): boolean {
  if (!pagado_hasta) return false;
  return pagado_hasta.slice(0, 10) < hoy;
}

/** Lo que el portal puede ver de un candidato. Espejo de SQL `portal_candidato_json()`. */
export function aPortal(c: Candidato, bloqueado: boolean): CandidatoPortal {
  return {
    id: c.id,
    vacante_id: c.vacante_id,
    bloqueado,
    veredicto: c.veredicto,
    etapa: c.etapa,
    columna: columnaDe(c.veredicto, c.etapa),
    ciudad: c.ciudad,
    recibido_en: c.recibido_en,
    ingresado_en: c.ingresado_en ?? c.recibido_en,
    criterios: (c.criterios ?? []).map((k) => ({
      clave: k.clave,
      estado: k.estado,
      dato: bloqueado ? null : k.dato,
    })),
    nombre: bloqueado ? null : c.nombre,
    telefono: bloqueado ? null : c.telefono,
    motivo: bloqueado ? null : c.motivo,
    respuestas: bloqueado ? null : (c.respuestas ?? {}),
    cv_url: bloqueado ? null : c.cv_url,
    nota_cliente: bloqueado ? null : c.nota_cliente,
    motivo_cliente: bloqueado || c.etapa !== "descartado" ? null : (c.motivo_cliente ?? null),
  };
}

/** Veredicto a partir de criterios (lo usa la demo y sirve de referencia para el bot). */
export function veredictoDesdeCriterios(
  crit: CriterioVacante[],
  res: CriterioCandidato[],
): { veredicto: Veredicto; motivo: string } {
  let rojo: string | null = null;
  let amarillo: string | null = null;
  for (const r of res) {
    const cv = crit.find((x) => x.clave === r.clave);
    const efecto = cv?.efecto ?? "marca";
    if (r.estado === "no" && efecto === "descarta") rojo ??= r.dato;
    else if (r.estado !== "ok") amarillo ??= r.dato;
  }
  if (rojo) return { veredicto: "descartado", motivo: rojo };
  if (amarillo) return { veredicto: "revisar", motivo: amarillo };
  return {
    veredicto: "cumple",
    motivo: `Cumple los ${crit.length} criterios de la vacante`,
  };
}

/** Teléfono a E.164. Números de 10 dígitos se toman como México (+52). */
export function normalizarTelefono(t: string | null | undefined): string | null {
  if (!t) return null;
  const limpio = String(t).trim();
  const dig = limpio.replace(/\D/g, "");
  if (!dig) return null;
  if (limpio.startsWith("+")) return "+" + dig;
  if (dig.length === 10) return "+52" + dig;
  if (dig.length === 12 && dig.startsWith("52")) return "+" + dig;
  if (dig.length === 13 && dig.startsWith("521")) return "+52" + dig.slice(3);
  return "+" + dig;
}

export function telefonoLegible(e164: string | null | undefined): string {
  if (!e164) return "—";
  const d = e164.replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("52")) {
    const n = d.slice(2);
    return `+52 ${n.slice(0, 2)} ${n.slice(2, 6)} ${n.slice(6)}`;
  }
  return e164;
}

export function primerNombre(n: string | null | undefined): string {
  return (n ?? "").trim().split(/\s+/)[0] ?? "";
}

export function linkWhatsApp(
  telefono: string | null | undefined,
  nombre: string | null | undefined,
  puesto: string | null | undefined,
  empresa?: string | null,
  plantilla?: string | null,
): string | null {
  if (!telefono) return null;
  const d = telefono.replace(/\D/g, "");
  const de = empresa ? ` de ${empresa}` : "";
  // Mensaje propio del cliente (clientes.mensaje_whatsapp) con {nombre}, {puesto} y {empresa}.
  const txt = plantilla?.trim()
    ? plantilla
        .replace(/\{nombre\}/g, primerNombre(nombre))
        .replace(/\{puesto\}/g, puesto ?? "la vacante")
        .replace(/\{empresa\}/g, empresa ?? "")
    : `Hola ${primerNombre(nombre)}, te escribo${de} por la vacante de ${puesto ?? "la vacante"}. ¿Tienes unos minutos para platicar?`;
  return `https://wa.me/${d}?text=${encodeURIComponent(txt)}`;
}

export function linkLlamar(telefono: string | null | undefined): string | null {
  return telefono ? `tel:${telefono}` : null;
}

/**
 * Campos que nunca se guardan ni se muestran (contrato, «Reglas»).
 * Se comparan sin acentos, en minúsculas y sin separadores.
 */
export const CAMPOS_PROHIBIDOS = [
  "edad",
  "age",
  "genero",
  "gender",
  "sexo",
  "sex",
  "estadocivil",
  "maritalstatus",
  "foto",
  "photo",
  "fotografia",
  "avatar",
  "fechadenacimiento",
  "fechanacimiento",
  "nacimiento",
  "birthdate",
  "dateofbirth",
];

export function claveNormalizada(k: string): string {
  return k
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

export function esCampoProhibido(k: string): boolean {
  const n = claveNormalizada(k);
  return CAMPOS_PROHIBIDOS.some((p) => n === p || n.startsWith(p));
}

/** Quita campos prohibidos de un objeto plano (y de preguntas de `respuestas`). */
export function sinCamposProhibidos<T extends Record<string, unknown>>(o: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) {
    if (esCampoProhibido(k)) continue;
    out[k] = v;
  }
  return out as T;
}

/** Preguntas del cuestionario que tocan temas prohibidos (se filtran de `respuestas`). */
export function esPreguntaProhibida(p: string): boolean {
  const n = claveNormalizada(p);
  return [
    "edad",
    "cuantosanos",
    "anostienes",
    "genero",
    "sexo",
    "estadocivil",
    "casado",
    "soltero",
    "foto",
    "nacimiento",
  ].some((x) => n.includes(x));
}

export function dinero(n: number | null | undefined): string {
  return (
    "$" +
    Number(n ?? 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })
  );
}

const MES = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];
export const MES_LARGO = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

/** «27 sep» */
export function fechaCorta(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [, m, d] = iso.slice(0, 10).split("-").map(Number);
  return `${d} ${MES[m - 1]}`;
}

/** «Octubre 2026» */
export function nombrePeriodo(iso: string): string {
  const [y, m] = iso.slice(0, 10).split("-").map(Number);
  return `${MES_LARGO[m - 1]} ${y}`;
}
